import type { Item, Tier } from "./types";

/**
 * The till's preview of tier prices (the server works it out again when the sale is made).
 * A line gets the lowest price it qualifies for: its group's price or a quantity break, never both.
 */
export function tierPrice(it: Pick<Item, "price" | "wholesalePrice" | "wholesaleMin" | "distributorPrice" | "distributorMin" | "breaks">, tier: Tier, qty: number): { price: number; label: string } {
  let best = { price: it.price, label: "retail" };
  const consider = (price: number | null | undefined, label: string, min: number | null | undefined) => {
    if (price == null || qty < Math.max(1, min ?? 1)) return;
    if (price < best.price) best = { price, label };
  };
  for (const b of it.breaks ?? []) consider(b.price, `break-${b.minQty}`, b.minQty);
  if (tier === "wholesale" || tier === "distributor") consider(it.wholesalePrice, "wholesale", it.wholesaleMin);
  if (tier === "distributor") consider(it.distributorPrice, "distributor", it.distributorMin);
  return best;
}

/** "Wholesale", "6+ price", "Distributor" for a line's tier tag; null for retail. */
export function tierTag(label: string | undefined): string | null {
  if (!label || label === "retail" || label === "override") return null;
  if (label.startsWith("break-")) return `${label.slice(6)}+ price`;
  return label === "wholesale" ? "Wholesale" : label === "distributor" ? "Distributor" : null;
}

/** The next cheaper price this item reaches with more units, for a "add N more" hint. */
export function nextBreak(it: Pick<Item, "price" | "wholesalePrice" | "wholesaleMin" | "distributorPrice" | "distributorMin" | "breaks">, tier: Tier, qty: number): { more: number; price: number } | null {
  const now = tierPrice(it, tier, qty).price;
  for (let q = qty + 1; q <= qty + 50; q++) {
    const p = tierPrice(it, tier, q).price;
    if (p < now) return { more: q - qty, price: p };
  }
  return null;
}
